import React from 'react';
import {
    Badge,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    Stack,
    Switch,
    FormControlLabel,
    Typography,
    Chip,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon, CheckCircle as CheckIcon, LocalOffer as LocalOfferIcon } from '@mui/icons-material';

import {
    BILLING_CYCLES,
    PLAN_ORDER,
    PLAN_TIERS,
    buildPlanKey,
    formatCurrency,
    getCycleSuffix,
    getDisplayPrice,
    getTierDefinition,
} from '../../../subscription';

function PricingCard({ tier, billingCycle, showInclVat, invoiceFeatures, isYearly, vatLabel, onStart }) {
    const definition = getTierDefinition(tier);
    const planKey = buildPlanKey(tier, billingCycle);
    const cycleSuffix = getCycleSuffix(billingCycle);
    const price = getDisplayPrice(planKey, showInclVat);
    const displayPrice = formatCurrency(price);
    const monthlyReference = getDisplayPrice(buildPlanKey(tier, BILLING_CYCLES.MONTHLY), showInclVat);
    const yearlyReference = getDisplayPrice(buildPlanKey(tier, BILLING_CYCLES.YEARLY), showInclVat);
    const savingValueRaw = isYearly ? monthlyReference * 12 - yearlyReference : 0;
    const savingValue = savingValueRaw > 0 ? formatCurrency(savingValueRaw) : null;
    const savingPct =
        savingValueRaw > 0 && monthlyReference > 0
            ? Math.round((savingValueRaw / (monthlyReference * 12)) * 1000) / 10
            : null;
    const savingPctLabel =
        savingPct != null
            ? `${savingPct.toLocaleString('de-DE', {
                minimumFractionDigits: savingPct % 1 ? 1 : 0,
                maximumFractionDigits: 1,
            })}%`
            : null;
    const badgeLabel = savingValue ? `Spare ${savingValue}${savingPctLabel ? ` (${savingPctLabel})` : ''}` : null;
    const effectiveMonthly = isYearly ? formatCurrency(yearlyReference / 12) : null;
    const features = tier === PLAN_TIERS.ACCOUNTING ? [...invoiceFeatures, ...definition.features] : definition.features;
    const uniqueFeatures = Array.from(new Set(features));
    const ctaLabel = tier === PLAN_TIERS.ACCOUNTING ? 'Buchhaltung starten' : 'Rechnungsmodul starten';
    const isAccountingTier = tier === PLAN_TIERS.ACCOUNTING;

    return (
        <Grid item xs={12} md={6} key={tier} sx={{ display: 'flex' }}>
            <Badge
                invisible={!badgeLabel}
                color="warning"
                sx={{ width: '100%' }}
                badgeContent={
                    badgeLabel ? (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <LocalOfferIcon fontSize="small" />
                            <Typography variant="caption" fontWeight={700}>
                                {badgeLabel}
                            </Typography>
                        </Stack>
                    ) : null
                }
            >
                <Card
                    sx={{
                        borderRadius: 3,
                        boxShadow: isAccountingTier ? '0 14px 36px rgba(255,152,0,0.25)' : '0 6px 24px rgba(0,0,0,0.08)',
                        border: isAccountingTier ? '2px solid rgba(255,152,0,0.6)' : '1px solid rgba(0,0,0,0.05)',
                        background: isAccountingTier
                            ? 'linear-gradient(180deg, rgba(255, 243, 224, 0.9) 0%, #fff 60%)'
                            : '#fff',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            gap: 3,
                        }}
                    >
                        <Stack spacing={2} sx={{ flexGrow: 1 }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                justifyContent="space-between"
                                alignItems={{ xs: 'flex-start', sm: 'center' }}
                                gap={2}
                            >
                                <Stack spacing={0.75}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="h6" fontWeight={700}>
                                            {definition.title}
                                        </Typography>
                                        {tier === PLAN_TIERS.ACCOUNTING && (
                                            <Chip size="small" color="warning" variant="outlined" label="Beliebt" />
                                        )}
                                    </Stack>
                                    <Typography color="text.secondary">{definition.subtitle}</Typography>
                                </Stack>
                                <Stack spacing={0.5} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                                    <Typography variant="h3" fontWeight={800}>
                                        {displayPrice} / {cycleSuffix}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        {vatLabel}
                                        {isYearly ? ' · jährliche Abrechnung' : ' · monatlich kündbar'}
                                    </Typography>
                                    {effectiveMonthly && (
                                        <Typography variant="body2" color="text.secondary">
                                            Effektiv {effectiveMonthly} / Monat
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                            <Divider />
                            <Grid container spacing={1}>
                                {uniqueFeatures.map((item) => (
                                    <Grid item xs={12} sm={6} key={item}>
                                        <Stack direction="row" alignItems="center" gap={1}>
                                            <CheckIcon fontSize="small" sx={{ color: 'orange' }} />
                                            <Typography>{item}</Typography>
                                        </Stack>
                                    </Grid>
                                ))}
                            </Grid>
                        </Stack>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={onStart}
                            sx={{
                                bgcolor: 'orange',
                                '&:hover': { bgcolor: '#cc7a00' },
                                borderRadius: 2,
                            }}
                            endIcon={<ArrowForwardIcon />}
                        >
                            {ctaLabel}
                        </Button>
                    </CardContent>
                </Card>
            </Badge>
        </Grid>
    );
}

export default function LandingPricingSection({
    invoiceNetMonthly,
    accountingNetMonthly,
    showInclVat,
    billingCycle,
    isYearly,
    vatLabel,
    invoiceFeatures,
    onStart,
    onToggleVat,
    onBillingCycleChange,
}) {
    return (
        <>
            <Typography variant="h4" align="center" fontWeight={800} gutterBottom>
                Einfaches, faires Pricing
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
                Rechnungsmodul ab {invoiceNetMonthly} netto, komplette Buchhaltung ab {accountingNetMonthly} netto. Monats- oder
                Jahreszahlung – du entscheidest.
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="center" gap={2} sx={{ mb: 3 }}>
                <FormControlLabel
                    control={<Switch checked={showInclVat} onChange={(event) => onToggleVat(event.target.checked)} />}
                    label="Preise inkl. USt (19 %) anzeigen"
                />
                <Stack direction="row" gap={1} alignItems="center">
                    <Button
                        size="small"
                        variant={billingCycle === BILLING_CYCLES.MONTHLY ? 'contained' : 'outlined'}
                        onClick={() => onBillingCycleChange(BILLING_CYCLES.MONTHLY)}
                        sx={{
                            borderRadius: 2,
                            bgcolor: billingCycle === BILLING_CYCLES.MONTHLY ? 'orange' : 'transparent',
                            '&:hover': { bgcolor: billingCycle === BILLING_CYCLES.MONTHLY ? '#cc7a00' : 'transparent' },
                        }}
                    >
                        Monatlich
                    </Button>
                    <Button
                        size="small"
                        variant={billingCycle === BILLING_CYCLES.YEARLY ? 'contained' : 'outlined'}
                        onClick={() => onBillingCycleChange(BILLING_CYCLES.YEARLY)}
                        sx={{
                            borderRadius: 2,
                            bgcolor: billingCycle === BILLING_CYCLES.YEARLY ? 'orange' : 'transparent',
                            '&:hover': { bgcolor: billingCycle === BILLING_CYCLES.YEARLY ? '#cc7a00' : 'transparent' },
                        }}
                    >
                        Jährlich
                    </Button>
                </Stack>
            </Stack>

            <Grid container spacing={4} justifyContent="center">
                {PLAN_ORDER.map((tier) => (
                    <PricingCard
                        key={tier}
                        tier={tier}
                        billingCycle={billingCycle}
                        showInclVat={showInclVat}
                        invoiceFeatures={invoiceFeatures}
                        isYearly={isYearly}
                        vatLabel={vatLabel}
                        onStart={onStart}
                    />
                ))}
            </Grid>
        </>
    );
}
